import { ComputablePricing } from "@ac-expo/api-types";
import { sumBy } from "lodash";
import { computeDiscount } from "../discount";
import { getExcludedTaxesPrice } from "../prices";
import { DiscountCodeToApply } from "./basket-pricing";

interface AppliedDiscountCode {
  discountCode: ComputablePricing["discountCodes"][0]["discountCode"];
  discountAmount: number;
}

interface ItemPriceComputation {
  basePrice: number;
  vatRate: number;

  appliedDegressivePrice: ComputablePricing["degressivePrices"][0] | null;
  degressiveDiscountAmount: number;
  priceAfterDegressive: number;

  appliedDiscountCodes: AppliedDiscountCode[];
  codeDiscountAmount: number;
  finalPrice: number;
}

interface ItemsPriceComputation {
  items: ItemPriceComputation[];
  pricingId: string;
  quantity: number;
  baseUnitPrice: number;
  vatRate: number;
  degressiveDiscountAmount: number;
  codeDiscountAmount: number;
  finalPrice: number;
  finalUnitPrice: number;
}

interface MultipleItemsPriceComputation {
  items: ItemsPriceComputation[];
  totalBasePrice: number;
  totalBasePriceExclTaxes: number;
  degressiveDiscountAmount: number;
  degressiveDiscountAmountExclTaxes: number;
  codeDiscountAmount: number;
  codeDiscountAmountExclTaxes: number;
  finalPrice: number;
  finalExclTaxesPrice: number;
}

const isDegressivePriceApplicable = (
  position: number,
  degressivePrice: ComputablePricing["degressivePrices"][0],
): boolean => {
  const minQuantity = degressivePrice.minQuantity || 1;
  const maxQuantity = degressivePrice.maxQuantity || Infinity;
  return position >= minQuantity && position <= maxQuantity;
};

const findApplicableDegressivePrice = (
  position: number,
  degressivePrices: ComputablePricing["degressivePrices"],
): ComputablePricing["degressivePrices"][0] | null => {
  if (!degressivePrices || degressivePrices.length === 0) {
    return null;
  }

  for (const degressivePrice of degressivePrices) {
    if (isDegressivePriceApplicable(position, degressivePrice)) {
      return degressivePrice;
    }
  }

  return null;
};

export const isQuantityOnQuote = (
  quantity: number,
  degressivePrices: ComputablePricing["degressivePrices"],
): boolean => {
  const applicableDegressivePrice = findApplicableDegressivePrice(quantity, degressivePrices);
  return applicableDegressivePrice?.onQuote ?? false;
};

const calculateDegressivePricing = (
  basePrice: number,
  position: number,
  degressivePrices: ComputablePricing["degressivePrices"],
) => {
  const appliedDegressivePrice = findApplicableDegressivePrice(position, degressivePrices);

  if (!appliedDegressivePrice) {
    return {
      appliedDegressivePrice: null,
      degressiveDiscountAmount: 0,
    };
  }

  const discount = appliedDegressivePrice.discount;
  const degressiveDiscountAmount = computeDiscount(basePrice, discount);

  return {
    appliedDegressivePrice,
    degressiveDiscountAmount,
  };
};

type AvailableDiscountCode = { id: string; discount: { type: string; value: number } | null };

const getAllAvailableDiscountCodes = (pricing: ComputablePricing): AvailableDiscountCode[] => {
  const specificCodes = pricing.discountCodes.map(({ discountCode }) => discountCode);
  const ticketingCodes = pricing.ticketing?.eventDiscountCodes ?? [];

  const allCodes = [...specificCodes, ...ticketingCodes];
  const uniqueCodes = allCodes.filter(
    (code, index, self) => self.findIndex((c) => c.id === code.id) === index,
  );

  return uniqueCodes;
};

const filterApplicableDiscountCodes = (
  appliedDiscountCodes: DiscountCodeToApply[],
  pricing: ComputablePricing,
): DiscountCodeToApply[] => {
  const availableDiscountCodes = getAllAvailableDiscountCodes(pricing);

  return appliedDiscountCodes.filter((appliedCode) =>
    availableDiscountCodes.some((code) => code.id === appliedCode.id),
  );
};

// Remaining units each limited code may still discount, shared by every unit of one pricing
// pass so a cap spans the whole basket, plus the eligible units each code had to skip once
// exhausted. Codes without `maxApplications` are never tracked.
export type DiscountCodeBudget = {
  remaining: Map<string, number>;
  skipped: Map<string, number>;
};

export const createDiscountCodeBudget = (
  discountCodes: DiscountCodeToApply[],
): DiscountCodeBudget => ({
  remaining: new Map(
    discountCodes.flatMap((code) =>
      typeof code.maxApplications === "number"
        ? [[code.id, Math.max(0, code.maxApplications)] as const]
        : [],
    ),
  ),
  skipped: new Map(),
});

// Code-unit order, the order lodash `sortBy` gives the local cart; never `localeCompare`.
export const compareGroupKeys = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

const withRemainingBudget = (
  discountCodes: DiscountCodeToApply[],
  budget: DiscountCodeBudget,
): DiscountCodeToApply[] =>
  discountCodes.filter((code) => {
    if ((budget.remaining.get(code.id) ?? Infinity) > 0) return true;
    budget.skipped.set(code.id, (budget.skipped.get(code.id) ?? 0) + 1);
    return false;
  });

const consumeBudget = (appliedDiscountCodes: AppliedDiscountCode[], budget: DiscountCodeBudget) => {
  for (const { discountCode } of appliedDiscountCodes) {
    const remaining = budget.remaining.get(discountCode.id);
    if (remaining !== undefined) budget.remaining.set(discountCode.id, remaining - 1);
  }
};

const applyDiscountCodes = (
  startingPrice: number,
  applicableDiscountCodes: DiscountCodeToApply[],
) => {
  let finalPrice = startingPrice;
  let codeDiscountAmount = 0;
  const appliedDiscountCodes: AppliedDiscountCode[] = [];

  for (const discountCode of applicableDiscountCodes) {
    if (discountCode.discount.type === "euro") {
      const discountAmount = computeDiscount(finalPrice, discountCode.discount);
      if (discountAmount > 0) {
        codeDiscountAmount += discountAmount;
        finalPrice = Math.max(0, finalPrice - discountAmount);
        appliedDiscountCodes.push({
          discountCode,
          discountAmount,
        });
      }
    }
  }

  for (const discountCode of applicableDiscountCodes) {
    if (discountCode.discount.type === "percentage") {
      const discountAmount = computeDiscount(finalPrice, discountCode.discount);
      if (discountAmount > 0) {
        codeDiscountAmount += discountAmount;
        finalPrice = Math.max(0, finalPrice - discountAmount);
        appliedDiscountCodes.push({
          discountCode,
          discountAmount,
        });
      }
    }
  }

  return {
    codeDiscountAmount,
    finalPrice: Math.max(0, finalPrice),
    appliedDiscountCodes,
  };
};

export const computeItemPrice = (
  groupPricing: ComputablePricing,
  position: number,
  discountCodesToApply: DiscountCodeToApply[],
  budget: DiscountCodeBudget = createDiscountCodeBudget(discountCodesToApply),
): ItemPriceComputation => {
  if (!groupPricing.isPaid) {
    return {
      basePrice: 0,
      vatRate: 0,
      appliedDegressivePrice: null,
      degressiveDiscountAmount: 0,
      priceAfterDegressive: 0,
      appliedDiscountCodes: [],
      codeDiscountAmount: 0,
      finalPrice: 0,
    };
  }
  if (!groupPricing.price) {
    throw new Error("Group pricing price is required");
  }

  const basePrice = groupPricing.price.priceTTC;
  const vatRate = groupPricing.price.vatRate;

  const { appliedDegressivePrice, degressiveDiscountAmount } = calculateDegressivePricing(
    basePrice,
    position,
    groupPricing.degressivePrices,
  );
  const priceAfterDegressive = Math.max(0, basePrice - degressiveDiscountAmount);

  const applicableDiscountCodes = withRemainingBudget(
    filterApplicableDiscountCodes(discountCodesToApply, groupPricing),
    budget,
  );
  const { codeDiscountAmount, finalPrice, appliedDiscountCodes } = applyDiscountCodes(
    priceAfterDegressive,
    applicableDiscountCodes,
  );
  consumeBudget(appliedDiscountCodes, budget);

  return {
    basePrice,
    vatRate,

    appliedDegressivePrice,
    degressiveDiscountAmount,
    priceAfterDegressive,

    appliedDiscountCodes,
    codeDiscountAmount,
    finalPrice,
  };
};

export const computeItemsPrice = (
  pricing: ComputablePricing,
  quantity: number,
  appliedDiscountCodes: DiscountCodeToApply[],
  budget: DiscountCodeBudget = createDiscountCodeBudget(appliedDiscountCodes),
): ItemsPriceComputation => {
  const items = Array.from({ length: quantity }, (_, index) =>
    computeItemPrice(pricing, index + 1, appliedDiscountCodes, budget),
  );

  const baseUnitPrice = pricing.price?.priceTTC ?? 0;
  const vatRate = pricing.price?.vatRate ?? 0;
  const degressiveDiscountAmount = sumBy(items, "degressiveDiscountAmount");
  const codeDiscountAmount = sumBy(items, "codeDiscountAmount");
  const finalPrice = sumBy(items, "finalPrice");
  const finalUnitPrice = finalPrice / quantity;

  return {
    items,
    pricingId: pricing.id,
    quantity,
    baseUnitPrice,
    vatRate,
    degressiveDiscountAmount,
    codeDiscountAmount,
    finalUnitPrice,
    finalPrice,
  };
};

export const computeMultipleItemsPrice = (
  items: Array<{
    pricing: ComputablePricing;
    quantity: number;
    appliedDiscountCodes: DiscountCodeToApply[];
  }>,
): MultipleItemsPriceComputation => {
  // One budget for the whole cart: the caller passes items in the order the discount must be
  // allocated in (the basket lib and the local cart both use the buyable item group key).
  const budget = createDiscountCodeBudget(items.flatMap((item) => item.appliedDiscountCodes));
  const allComputedItems = items.map(({ pricing, quantity, appliedDiscountCodes }) =>
    computeItemsPrice(pricing, quantity, appliedDiscountCodes, budget),
  );

  const allComputedItemsWithExclTaxes = allComputedItems.map((items) => ({
    ...items,
    unitExclTaxesPrice: getExcludedTaxesPrice(items.baseUnitPrice, items.vatRate),
    degressiveDiscountExclTaxesAmount: getExcludedTaxesPrice(
      items.degressiveDiscountAmount,
      items.vatRate,
    ),
    codeDiscountExclTaxesAmount: getExcludedTaxesPrice(items.codeDiscountAmount, items.vatRate),
    finalExclTaxesPrice: getExcludedTaxesPrice(items.finalPrice, items.vatRate),
  }));

  const totalBasePrice = sumBy(
    allComputedItems,
    ({ baseUnitPrice: unitPrice, quantity }) => unitPrice * quantity,
  );
  const totalBasePriceExclTaxes = sumBy(
    allComputedItemsWithExclTaxes,
    ({ unitExclTaxesPrice, quantity }) => unitExclTaxesPrice * quantity,
  );

  const degressiveDiscountAmount = sumBy(allComputedItems, "degressiveDiscountAmount");
  const degressiveDiscountAmountExclTaxes = sumBy(
    allComputedItemsWithExclTaxes,
    "degressiveDiscountExclTaxesAmount",
  );

  const codeDiscountAmount = sumBy(allComputedItems, "codeDiscountAmount");
  const codeDiscountAmountExclTaxes = sumBy(
    allComputedItemsWithExclTaxes,
    "codeDiscountExclTaxesAmount",
  );

  const finalPrice = sumBy(allComputedItems, "finalPrice");
  const finalExclTaxesPrice = sumBy(allComputedItemsWithExclTaxes, "finalExclTaxesPrice");

  return {
    items: allComputedItems,
    totalBasePrice,
    totalBasePriceExclTaxes,
    degressiveDiscountAmount,
    degressiveDiscountAmountExclTaxes,
    codeDiscountAmount,
    codeDiscountAmountExclTaxes,
    finalPrice,
    finalExclTaxesPrice,
  };
};
